import { Badge, type BadgeTon } from "./badge";
import { breiteKlasse, cx } from "./fortschritt/breite";

/**
 * Auslastung (Brötchen-Vorbestellung B2, Spec 2026-10-20, Entscheidungsvorlage Bedarf 2, Kandidat 2A; Design-Guide `auslastung.md`): wie voll
 * ein Ausgabefenster (oder ein anderer begrenzter Vorrat) ist, als Balken mit Wort und Pille. Drei Stufen: frei, fast voll, ausgebucht. Die
 * Farbe trägt nie allein die Aussage: das Wort unter dem Balken und die Pille sagen dasselbe (Idee 11: Worte statt Zahlen, keine „noch 3 Plätze“).
 *
 * Balkenfarben nur aus Theme-Tokens (`bg-status-ok`, `bg-status-warn`, `bg-destructive`; K3), Breite über die festen Breitenklassen (`wp0`…`wp100`,
 * kein `style=`). Rein darstellend: die Stufe rechnet der Aufrufer (Kantine: `auslastung` in `broetchen-bestell-sicht.ts`, dieselbe Rechnung wie der
 * Server).
 */
export type AuslastungStufe = "frei" | "fast_voll" | "ausgebucht";

const BALKEN: Record<AuslastungStufe, string> = {
  frei: "h-full rounded-full bg-status-ok",
  fast_voll: "h-full rounded-full bg-status-warn",
  ausgebucht: "h-full rounded-full bg-destructive",
};

const PILLE: Record<AuslastungStufe, BadgeTon> = { frei: "erfolg", fast_voll: "warnung", ausgebucht: "fehler" };

export function Auslastung({
  prozent,
  stufe,
  satz,
  pille,
  label = "Auslastung",
}: {
  /** Anteil 0 bis 100 (Breite des Balkens). */
  prozent: number;
  stufe: AuslastungStufe;
  /** Wort unter dem Balken („Fast voll, nur noch wenige Plätze“). */
  satz: string;
  /** Wort der Pille („Frei“, „Fast voll“, „Ausgebucht“). */
  pille: string;
  /** Name des Balkens für Hilfstechnik. */
  label?: string;
}) {
  const p = Math.min(100, Math.max(0, Math.round(prozent)));
  return (
    <>
      {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-progress-cards/goal-progress-bar-card (nur der Balken mit Beschriftung; Farben als Status-Tokens) */}
      <div className="flex min-w-0 items-center gap-x-3" data-auslastung={stufe}>
        <div className="grow min-w-0">
          <div className="flex w-full h-1.5 bg-surface-1 rounded-full overflow-hidden" role="progressbar" aria-label={label} aria-valuenow={p} aria-valuemin={0} aria-valuemax={100} aria-valuetext={satz}>
            <span className={cx(BALKEN[stufe], breiteKlasse(p))} />
          </div>
          <p className="mt-1 text-xs text-muted-foreground-1">{satz}</p>
        </div>
        <span className="shrink-0">
          <Badge ton={PILLE[stufe]}>{pille}</Badge>
        </span>
      </div>
    </>
  );
}
